/**
 * Fahrzeuge, Kunden, Orte — und das Zusammenführen doppelter Schreibweisen.
 *
 * Die drei Tabellen sehen verschieden aus, teilen aber dieselbe Geschichte:
 * In der SharePoint-Liste standen 214 Schreibweisen für 128 Orte. Deshalb geht
 * es hier nicht nur ums Anlegen und Ändern, sondern vor allem darum, den
 * Bestand wieder einzusammeln, wenn er auseinandergelaufen ist.
 *
 * Zwei Dinge, die man dem Aufbau ansieht:
 *
 * Stillegen statt löschen. `aktiv = false` nimmt einen Eintrag aus den
 * Auswahllisten, lässt aber jede alte Fahrt, die daran hängt, lesbar. Löschen
 * würde entweder scheitern (Fremdschlüssel) oder Geschichte zerreißen.
 *
 * Zusammenführen macht die DATENBANK, nicht dieser Code. An einem Ort hängen
 * Fuhrenblöcke, Konditionen, Streckenvorlagen und Aufträge; das einzeln
 * umzuhängen wäre eine Kette von Schritten, von denen jeder scheitern kann.
 * `ort_zusammenfuehren` erledigt es in einer Transaktion — ganz oder gar nicht.
 */

import { supabase } from '../supabase';
import {
  aendern,
  anlegen,
  aufrufen,
  entfernen,
  NEUER_STAND_GELADEN,
  Speicherfehler,
} from './speichern';
import { alsKlarfehler } from './fehler';
import { preisVorgang, type Preiswerte } from './vorlagenpreis';

/* --------------------------------------------------------------- Fahrzeuge */

export type Fahrzeug = {
  id: string;
  kennzeichen: string;
  bezeichnung: string | null;
  typ: string | null;
  kurzname: string | null;
  kurzname_eigen: string | null;
  aktiv: boolean;
  notiz: string | null;
  erstellt_am: string;
  geaendert_am: string;
};

export async function fahrzeugeLaden(): Promise<Fahrzeug[]> {
  const { data, error } = await supabase
    .from('fahrzeug')
    .select('*')
    .order('aktiv', { ascending: false })
    .order('kennzeichen');
  if (error) throw new Speicherfehler(alsKlarfehler(error));
  return data as Fahrzeug[];
}

export async function fahrzeugAnlegen(werte: {
  kennzeichen: string;
  bezeichnung?: string;
  typ?: string;
}): Promise<Fahrzeug> {
  return anlegen<Fahrzeug>('fahrzeug', {
    kennzeichen: werte.kennzeichen.trim().toUpperCase(),
    bezeichnung: leerZuNull(werte.bezeichnung),
    typ: leerZuNull(werte.typ),
  });
}

export async function fahrzeugAendern(
  fahrzeug: Fahrzeug,
  werte: Partial<Fahrzeug>,
): Promise<Fahrzeug> {
  return aendern<Fahrzeug>({
    tabelle: 'fahrzeug',
    id: fahrzeug.id,
    gelesenerStand: fahrzeug.geaendert_am,
    werte,
    was: `Das Fahrzeug ${fahrzeug.kennzeichen}`,
  });
}

/* ------------------------------------------------------------------ Kunden */

export type Kunde = {
  id: string;
  nummer: string | null;
  name: string;
  /**
   * Kurzform für Listen und die App. Leer = der volle Name wird gezeigt.
   * Auf Rechnungen und im Leistungsnachweis gilt IMMER `name`.
   */
  anzeigename: string | null;
  zusatz: string | null;
  strasse: string | null;
  plz: string | null;
  ort: string | null;
  land: string;
  ansprechpartner: string | null;
  telefon: string | null;
  email: string | null;
  rechnungsintervall: 'woechentlich' | 'monatlich' | 'je_auftrag';
  aktiv: boolean;
  bestaetigt: boolean;
  angelegt_von: string | null;
  zusammengefuehrt_in: string | null;
  alt_schreibweisen: string[];
  notiz: string | null;
  erstellt_am: string;
  geaendert_am: string;
};

export const INTERVALLTEXT: Record<Kunde['rechnungsintervall'], string> = {
  woechentlich: 'Wöchentlich',
  monatlich: 'Monatlich',
  je_auftrag: 'Je Auftrag',
};

export async function kundenLaden(): Promise<Kunde[]> {
  const { data, error } = await supabase
    .from('kunde')
    .select('*')
    .is('zusammengefuehrt_in', null)
    .order('aktiv', { ascending: false })
    .order('name');
  if (error) throw new Speicherfehler(alsKlarfehler(error));
  return data as Kunde[];
}

export async function kundeAnlegen(werte: {
  name: string;
  nummer?: string;
  strasse?: string;
  plz?: string;
  ort?: string;
}): Promise<Kunde> {
  return anlegen<Kunde>('kunde', {
    name: werte.name.trim(),
    nummer: leerZuNull(werte.nummer),
    strasse: leerZuNull(werte.strasse),
    plz: leerZuNull(werte.plz),
    ort: leerZuNull(werte.ort),
    // Vom Büro angelegt heißt: geprüft. Der Haken unterscheidet genau das von
    // dem, was ein Fahrer unterwegs eingetippt hat.
    bestaetigt: true,
  });
}

export async function kundeAendern(
  kunde: Kunde,
  werte: Partial<Kunde>,
): Promise<Kunde> {
  return aendern<Kunde>({
    tabelle: 'kunde',
    id: kunde.id,
    gelesenerStand: kunde.geaendert_am,
    werte,
    was: `Der Kunde ${kunde.name}`,
  });
}

/* -------------------------------------------------------------------- Orte */

export type Ort = {
  id: string;
  name: string;
  strasse: string | null;
  plz: string | null;
  ortsname: string | null;
  bestaetigt: boolean;
  angelegt_von: string | null;
  zusammengefuehrt_in: string | null;
  alt_schreibweisen: string[];
  aktiv: boolean;
  erstellt_am: string;
  geaendert_am: string;
};

export async function orteLaden(): Promise<Ort[]> {
  const { data, error } = await supabase
    .from('ort')
    .select('*')
    .is('zusammengefuehrt_in', null)
    .order('aktiv', { ascending: false })
    .order('name');
  if (error) throw new Speicherfehler(alsKlarfehler(error));
  return data as Ort[];
}

export async function ortAnlegen(werte: {
  name: string;
  strasse?: string;
  plz?: string;
  ortsname?: string;
}): Promise<Ort> {
  return anlegen<Ort>('ort', {
    name: werte.name.trim(),
    strasse: leerZuNull(werte.strasse),
    plz: leerZuNull(werte.plz),
    ortsname: leerZuNull(werte.ortsname),
    bestaetigt: true,
  });
}

export async function ortAendern(ort: Ort, werte: Partial<Ort>): Promise<Ort> {
  return aendern<Ort>({
    tabelle: 'ort',
    id: ort.id,
    gelesenerStand: ort.geaendert_am,
    werte,
    was: `Der Ort ${ort.name}`,
  });
}

/* --------------------------------------------------------- Zusammenführen */

/**
 * Beide Aufrufe geben die Kennung des Eintrags zurück, der übrig bleibt.
 *
 * Die Reihenfolge der Wörter ist hier wichtig und leicht zu verwechseln:
 * `von` verschwindet, `nach` bleibt. In der Oberfläche steht deshalb immer
 * ausgeschrieben, welcher Name danach noch da ist.
 */
export async function ortZusammenfuehren(
  von: string,
  nach: string,
): Promise<string> {
  return aufrufen<string>('ort_zusammenfuehren', { p_von: von, p_nach: nach });
}

export async function kundeZusammenfuehren(
  von: string,
  nach: string,
): Promise<string> {
  return aufrufen<string>('kunde_zusammenfuehren', { p_von: von, p_nach: nach });
}

/* ------------------------------------------------------------- Nachpflege */

/**
 * Was Fahrer unterwegs selbst angelegt haben und das Büro noch nicht gesehen
 * hat. Die Sicht liefert Kunden und Orte in einem Aufwasch, samt der Zahl der
 * Verwendungen — die entscheidet, wie dringend es ist: ein Ort, an dem schon
 * zwölf Fuhren hängen, ist keine Karteileiche mehr.
 */
export type Nachzupflegen = {
  art: 'kunde' | 'ort';
  id: string;
  name: string;
  erstellt_am: string;
  angelegt_von: string | null;
  angelegt_durch: string | null;
  verwendungen: number;
  /** Der Stand, gegen den Bestätigen und Umbenennen prüfen (H1.6). */
  geaendert_am: string;
};

export async function nachpflegeLaden(): Promise<Nachzupflegen[]> {
  const { data, error } = await supabase
    .from('nachzupflegen')
    .select('*')
    .order('erstellt_am', { ascending: false });
  if (error) throw new Speicherfehler(alsKlarfehler(error));
  return data as Nachzupflegen[];
}

/**
 * Ein unterwegs angelegter Kunde oder Ort, so wie „Offene Punkte" ihn zeigt —
 * MIT dem Stand, den der Bildschirm gelesen hat.
 */
export type Nachpflegesatz = Pick<Nachzupflegen, 'art' | 'id' | 'name' | 'geaendert_am'>;

/**
 * Bestätigen heißt: Das Büro hat draufgeschaut, der Eintrag darf bleiben.
 *
 * Über `aendern()` mit dem gelesenen Stand (H1.6). Vorher ging das Update nur
 * nach Kennung hinaus: Hatte eine Kollegin den Ort inzwischen umbenannt, wurde
 * der NEUE Name bestätigt, den hier niemand gesehen hatte — und ohne
 * `.select()` hätte auch ein Update, das gar keine Zeile traf, als Erfolg
 * gegolten.
 */
export async function bestaetigen(satz: Nachpflegesatz): Promise<void> {
  await nachpflegeAendern(satz, { bestaetigt: true });
}

/** Umbenennen bestätigt gleich mit: Wer den Namen geraderückt, hat hingeschaut. */
export async function umbenennen(satz: Nachpflegesatz, name: string): Promise<void> {
  await nachpflegeAendern(satz, { name: name.trim(), bestaetigt: true });
}

async function nachpflegeAendern(
  satz: Nachpflegesatz,
  werte: Record<string, unknown>,
): Promise<void> {
  await aendern({
    tabelle: satz.art,
    id: satz.id,
    gelesenerStand: satz.geaendert_am,
    werte,
    was: satz.art === 'ort' ? `Der Ort ${satz.name}` : `Der Kunde ${satz.name}`,
    danach: NEUER_STAND_GELADEN,
  });
}

function leerZuNull(wert?: string): string | null {
  const sauber = wert?.trim();
  return sauber ? sauber : null;
}

/* -------------------------------------------------------- Streckenvorlagen */

/**
 * Die immer gleichen Relationen im Pendelverkehr.
 *
 * Warum das Büro sie pflegt und nicht der Fahrer: Wer morgens am Werkstor
 * steht, soll auswählen, nicht eintippen. Jede getippte Strecke ist eine
 * mögliche neue Schreibweise — genau das, was wir bei Orten und Kunden mühsam
 * wieder einsammeln.
 *
 * Die Reihenfolge ist keine Kosmetik. Steht die Strecke, die einer zwanzig Mal
 * im Monat fährt, auf Platz sieben, tippt er sich jeden Morgen dorthin.
 */
export type Relationsvorlage = {
  id: string;
  bezeichnung: string | null;
  ladeort_id: string;
  entladeort_id: string;
  aktiv: boolean;
  sortierung: number;
  erstellt_am: string;
  geaendert_am: string;
};

export type Vorlagenzeile = Relationsvorlage & {
  ladeort: string;
  entladeort: string;
  /** Der Preisvorschlag — null, solange keiner gepflegt ist. */
  preisart: string | null;
  preis_vk: number | null;
  mengeneinheit: string | null;
  /**
   * `geaendert_am` des Preises — `null` heißt: Beim Laden stand KEIN Preis da.
   * Daran entscheidet `preisVorgang()` zwischen Anlegen und Ändern (H1.6).
   */
  preis_geaendert_am: string | null;
};

export async function vorlagenLaden(): Promise<Vorlagenzeile[]> {
  // Bewusst getrennte Abfragen statt einer Verknüpfung über PostgREST: Die
  // Vorlage zeigt ZWEIMAL auf dieselbe Tabelle, und mehrdeutige Verknüpfungen
  // liefern dort im Zweifel stillschweigend nichts.
  const [vorlagen, orte, preise] = await Promise.all([
    supabase
      .from('relationsvorlage')
      .select('*')
      .order('aktiv', { ascending: false })
      .order('sortierung'),
    supabase.from('ort').select('id, name'),
    // Eigene Tabelle, eigene Abfrage: Der Fahrer bekommt hier null Zeilen
    // zurück, und zwar durch die Zeilensicherheit der Datenbank und nicht,
    // weil jemand die Spalte weggelassen hat. Ein Fehler ist das für ihn
    // nicht — er sieht die Vorlagen weiterhin vollständig, nur ohne Preis.
    supabase.from('relationsvorlage_preis').select('*'),
  ]);

  if (vorlagen.error) throw new Speicherfehler(alsKlarfehler(vorlagen.error));
  if (orte.error) throw new Speicherfehler(alsKlarfehler(orte.error));

  const nachKennung = new Map(
    (orte.data as { id: string; name: string }[]).map((o) => [o.id, o.name]),
  );

  const preisNach = new Map(
    (
      (preise.data ?? []) as {
        relationsvorlage_id: string;
        preisart: string;
        preis_vk: number | null;
        mengeneinheit: string | null;
        geaendert_am: string;
      }[]
    ).map((p) => [p.relationsvorlage_id, p]),
  );

  return (vorlagen.data as Relationsvorlage[]).map((v) => {
    const preis = preisNach.get(v.id);
    return {
      ...v,
      ladeort: nachKennung.get(v.ladeort_id) ?? 'unbekannt',
      entladeort: nachKennung.get(v.entladeort_id) ?? 'unbekannt',
      preisart: preis?.preisart ?? null,
      preis_vk: preis?.preis_vk !== undefined && preis?.preis_vk !== null
        ? Number(preis.preis_vk)
        : null,
      mengeneinheit: preis?.mengeneinheit ?? null,
      preis_geaendert_am: preis?.geaendert_am ?? null,
    };
  });
}

/**
 * Den Preisvorschlag einer Strecke setzen oder entfernen.
 *
 * ═══ WARUM DAS EIN VORSCHLAG IST UND KEINE BINDUNG ═══
 *
 * Der Preis wandert beim Eintragen einer Tour EINMAL in den Auftrag und lebt
 * dort weiter. Ändert sich die Vorlage später, bleiben bereits eingetragene
 * Touren unberührt — sonst würde eine Preiserhöhung im Oktober rückwirkend
 * den September teurer machen, und die Auswertung eines abgeschlossenen
 * Monats stünde nie fest.
 */
export async function vorlagenpreisSetzen(
  vorlage: Pick<Vorlagenzeile, 'id' | 'preis_geaendert_am'>,
  werte: Preiswerte,
): Promise<void> {
  // ═══ H1.6: VIER FÄLLE STATT EINES UPSERTS ═══
  //
  // Der Upsert ersetzte still, was eine Kollegin inzwischen eingetragen hatte,
  // und das Delete räumte einen Preis weg, den hier niemand gesehen hatte.
  // Welcher Fall vorliegt, entscheidet `preisVorgang()` — getrennt, damit es
  // ohne Browser prüfbar ist (`npm run logik`).
  const vorgang = preisVorgang(vorlage.preis_geaendert_am, werte);
  const was = 'Der Preisvorschlag dieser Strecke';

  if (vorgang === 'nichts') return;

  if (vorgang === 'entfernen') {
    await entfernen({
      tabelle: 'relationsvorlage_preis',
      schluessel: 'relationsvorlage_id',
      id: vorlage.id,
      gelesenerStand: vorlage.preis_geaendert_am!,
      was,
      danach: NEUER_STAND_GELADEN,
    });
    return;
  }

  const zeile = {
    preisart: werte.preisart,
    preis_vk: werte.preis_vk,
    mengeneinheit: werte.preisart === 'menge' ? werte.mengeneinheit : null,
  };

  if (vorgang === 'aendern') {
    await aendern({
      tabelle: 'relationsvorlage_preis',
      schluessel: 'relationsvorlage_id',
      id: vorlage.id,
      gelesenerStand: vorlage.preis_geaendert_am!,
      werte: zeile,
      was,
      danach: NEUER_STAND_GELADEN,
    });
    return;
  }

  // Anlegen: ein Insert, KEIN Upsert. Der Primärschlüssel ist die Vorlage —
  // stand beim Öffnen kein Preis da und steht jetzt einer, meldet die
  // Datenbank 23505. Genau das ist der Fall „gleichzeitig angelegt", und er
  // verdient einen eigenen Satz statt „Das gibt es schon".
  try {
    await anlegen('relationsvorlage_preis', {
      relationsvorlage_id: vorlage.id,
      ...zeile,
    });
  } catch (problem) {
    if (problem instanceof Speicherfehler && problem.klar.art === 'doppelt') {
      throw new Speicherfehler({
        art: 'gleichzeitig_geaendert',
        titel: 'Jemand war schneller',
        text: 'Für diese Strecke hat inzwischen jemand anderes gleichzeitig einen Preis angelegt. Es wurde NICHTS gespeichert. Die Liste ist neu geladen — schau dir den Preis an, der jetzt dasteht, und ändere ihn, falls deiner gelten soll.',
        original: problem.klar.original,
      });
    }
    throw problem;
  }
}

export async function vorlageAnlegen(werte: {
  ladeortId: string;
  entladeortId: string;
  bezeichnung?: string;
}): Promise<Relationsvorlage> {
  return anlegen<Relationsvorlage>('relationsvorlage', {
    ladeort_id: werte.ladeortId,
    entladeort_id: werte.entladeortId,
    bezeichnung: leerZuNull(werte.bezeichnung),
  });
}

export async function vorlageAendern(
  vorlage: Relationsvorlage,
  werte: Partial<Relationsvorlage>,
): Promise<Relationsvorlage> {
  return aendern<Relationsvorlage>({
    tabelle: 'relationsvorlage',
    id: vorlage.id,
    gelesenerStand: vorlage.geaendert_am,
    werte,
    was: 'Die Streckenvorlage',
  });
}

/**
 * Die ganze neue Reihenfolge auf einmal.
 *
 * Nicht zwei vertauschte Werte: Das wären zwei Schreibvorgänge, von denen der
 * zweite scheitern kann — und danach stünden zwei Vorlagen auf derselben Zahl.
 */
export async function vorlagenSortieren(ids: string[]): Promise<void> {
  await aufrufen<void>('relationsvorlagen_sortieren', { p_ids: ids });
}
